import { useCallback, useEffect, useState } from 'react';
import { Check, Door, HouseLine, PencilSimple, Plus, Trash, X } from '@phosphor-icons/react';
import { BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, TextFeld } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { hausAdresse, naechsteHausBezeichnung, sortiereHaeuser, type Haus } from '../../../../../lib/objekte/haeuser';
import { useObjektSeite } from '../ObjektSeitenKontext';

interface EinheitZeile { id: string; bezeichnung: string | null; haus_id: string | null }
interface Entwurf { id?: string; bezeichnung: string; strasse: string; hausnummer: string; plz: string; stadt: string; notizen: string }

const leer = (s: string) => s.trim() || null;

/**
 * „Häuser verwalten“ (Liegenschaft mit mehreren Gebäuden) im neuen Design – Funktionen wie das
 * bisherige Häuser-Formular: Häuser ändern und löschen, Einheiten zuordnen, neue Häuser anlegen.
 * Löschen: Einheiten und Rechnungen fallen per ON DELETE SET NULL auf „ohne Haus“ zurück.
 */
export function HaeuserVerwaltung({ onSchliessen, onNeuesHaus, onGeaendert, zeige }: {
  onSchliessen: () => void;
  onNeuesHaus: () => void;
  onGeaendert: () => void;
  zeige: (text: string, fehler?: boolean) => void;
}) {
  const { daten } = useObjektSeite();
  const objektId = daten.objekt.id;
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [einheiten, setEinheiten] = useState<EinheitZeile[]>([]);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [zuordnen, setZuordnen] = useState<Haus | null>(null);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [loeschen, setLoeschen] = useState<Haus | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const lade = useCallback(async () => {
    const [h, e] = await Promise.all([
      supabase.from('objekt_haeuser').select('*').eq('objekt_id', objektId),
      supabase.from('objekt_einheiten').select('id, bezeichnung, haus_id').eq('objekt_id', objektId).order('bezeichnung'),
    ]);
    if (h.error || e.error) { setFehler((h.error ?? e.error)?.message ?? 'Laden fehlgeschlagen'); return; }
    setHaeuser(sortiereHaeuser((h.data as Haus[] | null) ?? []));
    setEinheiten((e.data as EinheitZeile[] | null) ?? []);
  }, [objektId]);

  useEffect(() => { void lade(); }, [lade]);

  const anzahl = (id: string) => einheiten.filter(e => e.haus_id === id).length;
  const fertig = async (meldung: string) => { await lade(); onGeaendert(); zeige(meldung); };

  const speichereHaus = async () => {
    if (!entwurf?.id) return;
    const bezeichnung = entwurf.bezeichnung.trim();
    if (!bezeichnung) { setFehler('Bitte eine Bezeichnung angeben.'); return; }
    if (haeuser.some(h => h.id !== entwurf.id && h.bezeichnung.trim().toLowerCase() === bezeichnung.toLowerCase())) { setFehler(`„${bezeichnung}“ gibt es schon.`); return; }
    setLaeuft(true); setFehler(null);
    const { error } = await supabase.from('objekt_haeuser').update({
      bezeichnung, strasse: leer(entwurf.strasse), hausnummer: leer(entwurf.hausnummer), plz: leer(entwurf.plz), stadt: leer(entwurf.stadt),
      notizen: leer(entwurf.notizen), updated_at: new Date().toISOString(),
    }).eq('id', entwurf.id);
    setLaeuft(false);
    if (error) { setFehler(error.message); return; }
    setEntwurf(null);
    await fertig(`${bezeichnung} gespeichert`);
  };

  const speichereZuordnung = async () => {
    if (!zuordnen) return;
    const hinzu = einheiten.filter(e => auswahl.has(e.id) && e.haus_id !== zuordnen.id).map(e => e.id);
    const weg = einheiten.filter(e => !auswahl.has(e.id) && e.haus_id === zuordnen.id).map(e => e.id);
    setLaeuft(true); setFehler(null);
    try {
      if (hinzu.length) { const r = await supabase.from('objekt_einheiten').update({ haus_id: zuordnen.id }).in('id', hinzu); if (r.error) throw new Error(r.error.message); }
      if (weg.length) { const r = await supabase.from('objekt_einheiten').update({ haus_id: null }).in('id', weg); if (r.error) throw new Error(r.error.message); }
      const name = zuordnen.bezeichnung;
      setZuordnen(null);
      await fertig(`Einheiten von ${name} gespeichert`);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Zuordnung fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLaeuft(true);
    const { error } = await supabase.from('objekt_haeuser').delete().eq('id', loeschen.id);
    setLaeuft(false);
    if (error) { setFehler(error.message); setLoeschen(null); return; }
    const name = loeschen.bezeichnung;
    setLoeschen(null);
    await fertig(`${name} gelöscht`);
  };

  const schliessenKnopf = (
    <button type="button" aria-label="Schließen" onClick={onSchliessen} className="flex items-center justify-center cursor-pointer"
      style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>
  );

  return (
    <InnenKarte>
      <KartenTitel icon={HouseLine} rechts={schliessenKnopf}>Häuser verwalten</KartenTitel>
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Mehrere Gebäude mit eigenen Adressen. Ohne Häuser stehen die Einheiten einfach untereinander.</span>
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}

      {haeuser.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch kein Haus angelegt.</span>}
      <div className="flex flex-col">
        {haeuser.map(h => {
          const bearbeitet = entwurf?.id === h.id;
          const ordnetZu = zuordnen?.id === h.id;
          return (
            <div key={h.id} className="flex flex-col" style={{ gap: 10, padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
              <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--acc-soft)' }}><HouseLine weight="fill" size={18} color="var(--acc)" /></span>
                <span className="flex flex-col flex-1 min-w-0" style={{ minWidth: 160 }}>
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{h.bezeichnung}</span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{hausAdresse(h) || 'ohne eigene Adresse'} · {anzahl(h.id)} {anzahl(h.id) === 1 ? 'Einheit' : 'Einheiten'}</span>
                </span>
                <ImmoButton groesse={32} icon={Door} disabled={!!entwurf || (!!zuordnen && !ordnetZu)}
                  onClick={() => { setFehler(null); setZuordnen(h); setAuswahl(new Set(einheiten.filter(e => e.haus_id === h.id).map(e => e.id))); }}>Einheiten</ImmoButton>
                <ImmoButton groesse={32} icon={PencilSimple} aria-label={`${h.bezeichnung} ändern`} title="Haus ändern" disabled={!!zuordnen || (!!entwurf && !bearbeitet)}
                  onClick={() => { setFehler(null); setEntwurf({ id: h.id, bezeichnung: h.bezeichnung, strasse: h.strasse ?? '', hausnummer: h.hausnummer ?? '', plz: h.plz ?? '', stadt: h.stadt ?? '', notizen: h.notizen ?? '' }); }} />
                <ImmoButton groesse={32} art="bad" icon={Trash} aria-label={`${h.bezeichnung} löschen`} title="Haus löschen" disabled={!!entwurf || !!zuordnen} onClick={() => setLoeschen(h)} />
              </div>

              {bearbeitet && entwurf && (
                <div className="flex flex-col" style={{ gap: 10, padding: 12, borderRadius: 18, background: 'var(--fill)' }}>
                  <FeldRaster minBreite={150}>
                    <TextFeld label="Bezeichnung" wert={entwurf.bezeichnung} onChange={v => setEntwurf({ ...entwurf, bezeichnung: v })} />
                    <TextFeld label="Straße" wert={entwurf.strasse} onChange={v => setEntwurf({ ...entwurf, strasse: v })} />
                    <TextFeld label="Hausnr." wert={entwurf.hausnummer} onChange={v => setEntwurf({ ...entwurf, hausnummer: v })} />
                    <TextFeld label="PLZ" wert={entwurf.plz} onChange={v => setEntwurf({ ...entwurf, plz: v })} inputMode="numeric" />
                    <TextFeld label="Ort" wert={entwurf.stadt} onChange={v => setEntwurf({ ...entwurf, stadt: v })} />
                    <TextFeld label="Notiz (optional)" wert={entwurf.notizen} onChange={v => setEntwurf({ ...entwurf, notizen: v })} />
                  </FeldRaster>
                  <div className="flex justify-end" style={{ gap: 8 }}>
                    <ImmoButton onClick={() => { setEntwurf(null); setFehler(null); }}>Abbrechen</ImmoButton>
                    <ImmoButton art="akzent" disabled={laeuft} onClick={() => { void speichereHaus(); }}>{laeuft ? 'Speichert …' : 'Speichern'}</ImmoButton>
                  </div>
                </div>
              )}

              {ordnetZu && (
                <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 18, background: 'var(--fill)' }}>
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Angehakte Einheiten gehören zu {h.bezeichnung}. Eine Einheit aus einem anderen Haus wechselt beim Anhaken hierher.</span>
                  {einheiten.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Diese Liegenschaft hat noch keine Einheiten.</span>}
                  <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(200px,100%),1fr))', gap: 6 }}>
                    {einheiten.map(e => {
                      const an = auswahl.has(e.id);
                      const anderes = e.haus_id && e.haus_id !== h.id ? haeuser.find(x => x.id === e.haus_id)?.bezeichnung : null;
                      return (
                        <button key={e.id} type="button" role="checkbox" aria-checked={an}
                          onClick={() => setAuswahl(s => { const n = new Set(s); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; })}
                          className="flex items-center text-left cursor-pointer"
                          style={{ gap: 10, padding: '10px 12px', borderRadius: 14, border: 'none', background: an ? 'var(--bg)' : 'var(--fill2)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'none', color: 'var(--ink)' }}>
                          <span className="flex items-center justify-center shrink-0" style={{ width: 20, height: 20, borderRadius: 6, background: an ? 'var(--acc-fill)' : 'transparent', boxShadow: an ? 'none' : 'inset 0 0 0 1.5px var(--line)', color: 'var(--acc-ink)' }}>
                            {an && <Check size={12} weight="bold" />}
                          </span>
                          <span className="truncate flex-1" style={{ fontSize: 13, fontWeight: 700 }}>{e.bezeichnung || 'Ohne Bezeichnung'}</span>
                          {anderes && <span className="shrink-0" style={{ fontSize: 10, fontWeight: 800, color: 'var(--ink3)' }}>{anderes}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end" style={{ gap: 8 }}>
                    <ImmoButton onClick={() => { setZuordnen(null); setFehler(null); }}>Abbrechen</ImmoButton>
                    <ImmoButton art="akzent" disabled={laeuft} onClick={() => { void speichereZuordnung(); }}>{laeuft ? 'Speichert …' : 'Übernehmen'}</ImmoButton>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ImmoButton icon={Plus} disabled={!!entwurf || !!zuordnen} onClick={onNeuesHaus} style={{ alignSelf: 'flex-start' }}>
        Haus hinzufügen{haeuser.length ? '' : ` („${naechsteHausBezeichnung(haeuser)}“)`}
      </ImmoButton>

      {loeschen && (
        <BestaetigenDialog titel={`„${loeschen.bezeichnung}“ löschen?`}
          text={anzahl(loeschen.id) > 0
            ? `${anzahl(loeschen.id)} ${anzahl(loeschen.id) === 1 ? 'Einheit' : 'Einheiten'} und alle zugeordneten Rechnungen bleiben erhalten und stehen danach unter „Ohne Haus“.`
            : 'Diesem Haus ist keine Einheit zugeordnet.'}
          bestaetigenLabel="Löschen" laeuft={laeuft} onBestaetigen={() => { void bestaetigeLoeschen(); }} onAbbrechen={() => setLoeschen(null)} />
      )}
    </InnenKarte>
  );
}
